import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Divider, Link, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useUserService } from '../../services/user.service';
import { formatFirebaseAuthError } from '../../utils/errorHandling';
import { LoginFormData, loginSchema } from '../../utils/validation/auth.schema';
import AuthLayout from './AuthLayout';
import FormTextField from './FormTextField';
import { hideCelloReferralWidget } from '../../utils/domUtils';

const LoginPage = () => {
    const navigate = useNavigate();
    const { login, loginWithGoogle } = useAuth();
    const { t } = useTranslation();
    const [errorKey, setErrorKey] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const userService = useUserService();

    // Get redirect path from session storage (set in PrivateRoute)
    const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/';

    const {
        control,
        handleSubmit: formSubmit,
        watch,
        formState: { isSubmitting, isValid, isDirty },
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
        },
        mode: 'onChange',
    });

    // Watch form values for validation
    const email = watch('email');
    const password = watch('password');

    // Check if form is valid for submit button
    const isFormValid = isValid && isDirty && email && password;

    const handleSubmit = async (data: LoginFormData) => {
        try {
            setErrorKey(null);
            setLoading(true);
            await login(data.email, data.password);
            userService.log('Log in with email');
            hideCelloReferralWidget();

            // Clear redirect path from session storage
            sessionStorage.removeItem('redirectAfterLogin');

            // Redirect to the intended page or dashboard
            navigate(redirectPath);
        } catch (err: any) {
            setErrorKey(formatFirebaseAuthError(err));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            setErrorKey(null);
            setLoading(true);
            
            // Store the information needed after redirect
            sessionStorage.setItem('googleAuthIntent', 'login');
            sessionStorage.setItem('googleAuthRedirect', redirectPath);
            
            // This will redirect to Google
            await loginWithGoogle();
            
            // Code below won't execute immediately due to redirect
        } catch (err: any) {
            setErrorKey(formatFirebaseAuthError(err));
            console.error(err);
            setLoading(false);
        }
    };
    
    const subtitleElement = (
        <Box sx={{ fontSize: '0.95rem' }}>
            {t('auth.login.noAccount')} <Link
                component={RouterLink}
                to="/signup"
                sx={{
                    color: 'text.primary',
                    textDecoration: 'underline',
                    '&:hover': {
                        color: 'text.secondary',
                    },
                }}
            >
                {t('auth.login.signUpLink')}
            </Link>
        </Box>
    );

    return (
        <AuthLayout 
            title={t('auth.login.title')}
            subtitle={subtitleElement}
        >
                
                {errorKey && (
                    <Alert
                        severity="error"
                        sx={{
                            marginBottom: '24px',
                            borderRadius: 1,
                        }}
                        role="alert"
                        aria-live="assertive"
                    >
                        {t(errorKey)}
                    </Alert>
                )}

                <Box
                    component="form"
                    onSubmit={formSubmit(handleSubmit)}
                    sx={{
                        width: '100%',
                    }}
                    noValidate
                >
                    <Stack spacing={2}>
                        <FormTextField
                            required
                            fullWidth
                            label={t('auth.email')}
                            name="email"
                            type="email"
                            autoComplete="email"
                            control={control}
                            inputProps={{
                                'aria-label': t('auth.email'),
                            }}
                            disabled={isSubmitting || loading}
                        />

                        <FormTextField
                            required
                            fullWidth
                            label={t('auth.password')}
                            autoComplete="current-password"
                            name="password"
                            control={control}
                            showPasswordToggle
                            inputProps={{
                                'aria-label': t('auth.password'),
                            }}
                            disabled={isSubmitting || loading}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    marginTop: '4px',
                                    marginBottom: '20px',
                                    fontSize: '0.85rem',
                                    opacity: 0.6
                                }}
                            >
                            <Link
                                component={RouterLink}
                                to="/forgot-password"
                                variant="body2"
                                aria-label={t('auth.login.forgotPasswordAriaLabel')}
                                sx={{
                                    color: 'text.primary',
                                    textDecoration: 'underline',
                                    fontWeight: 400,
                                    '&:hover': {
                                        color: 'text.secondary',
                                    },
                                }}
                            >
                                {t('auth.login.forgotPassword')}
                            </Link>
                            </Typography>
                        </Box>

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={isSubmitting || loading || !isFormValid}
                            sx={{
                                mt: 2,
                                fontWeight: 600,
                                textTransform: 'none',
                                fontSize: '1rem',
                                height: '48px',
                                borderRadius: 1,
                                boxShadow: 1,
                                '&:hover': {
                                    boxShadow: 2,
                                },
                            }}
                            aria-label={t('auth.login.loginButtonAriaLabel')}
                        >
                            {t('auth.login.submitButton')}
                        </Button>
                    </Stack>
                </Box>

                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{
                        margin: '32px 0',
                        width: '100%',
                    }}
                >
                    <Divider
                        sx={{
                            flexGrow: 1,
                            bgcolor: 'divider',
                        }}
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, fontSize: '0.85rem', opacity: 0.6 }}>
                        {t('auth.login.or')}
                    </Typography>
                    <Divider
                        sx={{
                            flexGrow: 1,
                            bgcolor: 'divider',
                        }}
                    />
                </Stack>

                <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleGoogleSignIn}
                    disabled={isSubmitting || loading}
                    sx={{
                    width: '100%',
                    height: '48px',
                    borderWidth: '1.5px',
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem',
                    marginBottom: '12px',
                    borderRadius: 1,
                    '&:hover': {
                        backgroundColor: 'action.hover',
                        borderColor: 'primary.light',
                    },
                }}
                    aria-label={t('auth.login.loginWithGoogleAriaLabel')}
                    startIcon={
                        <Box
                            component="span"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '8px',
                            }}
                        >
                            <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                                <path
                                    fill="#EA4335"
                                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                                />
                                <path
                                    fill="#4285F4"
                                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                                />
                                <path
                                    fill="#FBBC05"
                                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                                />
                                <path
                                    fill="#34A853"
                                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                                />
                            </svg>
                        </Box>
                    }
                >
                    {t('auth.login.loginWithGoogle')}
                </Button>

            </AuthLayout>
    );
};

export default LoginPage;
